import axios, { AxiosRequestConfig } from "axios";
import { UserManager } from "oidc-client";
import oidcConfig from "../config/oidc-config";
import { getPortalClientId } from "./portalClientId";

const priorAuthBaseUrl = process.env.REACT_APP_PRIOR_AUTH_API_URL;

const userManager = new UserManager(oidcConfig);

export class PriorAuthUnauthorizedError extends Error {
  readonly status: number;

  constructor(
    message = "Not authenticated for Prior Auth API",
    status = 401
  ) {
    super(message);
    this.name = "PriorAuthUnauthorizedError";
    this.status = status;
  }
}

export function isPriorAuthUnauthorized(error: unknown): boolean {
  if (error instanceof PriorAuthUnauthorizedError) {
    return true;
  }
  return (
    axios.isAxiosError(error) &&
    (error.response?.status === 401 || error.response?.status === 403)
  );
}

export class PriorAuthHttp {
  private static axios = axios.create({
    baseURL: priorAuthBaseUrl,
    headers: {
      "Content-Type": "application/json",
    },
  });

  /** Resolve a fresh OIDC access token; sync localStorage used by other portal callers. */
  private static async resolveAccessToken(): Promise<string | null> {
    const stored = localStorage.getItem("PortalAdmin-AccessToken");

    try {
      let user = await userManager.getUser();
      if (user?.expired) {
        try {
          user = await userManager.signinSilent();
        } catch {
          // Silent renew unavailable; fall through.
        }
      }

      if (user && !user.expired && user.access_token) {
        localStorage.setItem("PortalAdmin-AccessToken", user.access_token);
        return user.access_token;
      }
    } catch {
      // Fall back to stored token if OIDC lookup fails.
    }

    return stored || null;
  }

  private static async authHeaders(): Promise<Record<string, string>> {
    const token = await PriorAuthHttp.resolveAccessToken();
    const headers: Record<string, string> = {
      ClientId: getPortalClientId(),
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  private static async requestHeaders(
    config?: AxiosRequestConfig
  ): Promise<Record<string, string>> {
    const auth = await PriorAuthHttp.authHeaders();
    if (!auth.Authorization) {
      throw new PriorAuthUnauthorizedError(
        "Missing access token for Prior Auth API"
      );
    }

    return {
      ...auth,
      ...((config?.headers as Record<string, string>) || {}),
      ClientId: getPortalClientId(),
    };
  }

  private static rethrowAuth(error: unknown): never {
    if (isPriorAuthUnauthorized(error)) {
      const status = axios.isAxiosError(error)
        ? error.response?.status ?? 401
        : error instanceof PriorAuthUnauthorizedError
          ? error.status
          : 401;
      throw new PriorAuthUnauthorizedError(
        "Unauthorized for Prior Auth API",
        status
      );
    }
    throw error;
  }

  static async get(url: string, config?: AxiosRequestConfig) {
    const headers = await PriorAuthHttp.requestHeaders(config);
    try {
      const response = await PriorAuthHttp.axios.get(url, {
        ...config,
        headers,
      });
      return response.data;
    } catch (error) {
      PriorAuthHttp.rethrowAuth(error);
    }
  }

  static async put(url: string, body?: object, config?: AxiosRequestConfig) {
    const headers = await PriorAuthHttp.requestHeaders(config);
    try {
      const response = await PriorAuthHttp.axios.put(url, body, {
        ...config,
        headers,
      });
      return response.data;
    } catch (error) {
      PriorAuthHttp.rethrowAuth(error);
    }
  }
}
